import React, { useState, useContext, useEffect } from "react";
import { useQuery } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { Share2, Download, Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import CallDetailsHeader from "./components/CallDetailsHeader";
import CallTranscript from "./components/CallTranscript";
import CallRightPanel from "./components/CallRightPanel";
import { getCallDetails, getCallMetadata } from "../../../apis/callLogs";
import SharedDataContext from "../../../SharedDataContext";
import Cookies from "js-cookie";
import NoData from "../../NoData";
import RecordingOnlyView from "./components/RecordingOnlyView";
import TranscriptionLoadingView from "./components/TranscriptionLoadingView";
import { Skeleton } from "../../ui/skeleton";
import AccessDeniedView from "./components/AccessDeniedView";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import { extractCredentialsAndCleanUrl } from "../../../utils/GenericFn";
export default function CallDetails({ callId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const captureEvent = useCaptureEvent();

  // State for 404 error handling
  const [analysisNotFound, setAnalysisNotFound] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [callMetadata, setCallMetadata] = useState(null);
  const [providerRecordingId, setProviderRecordingId] = useState(null);
  // state declarations
  const [accessDenied, setAccessDenied] = useState(false);
  // Polling state
  const [isPollingTranscription, setIsPollingTranscription] = useState(false);
  const [transcriptionStatus, setTranscriptionStatus] = useState(null);
  const recordingUrl = location.state?.recordingUrl;
  const locationCallMetadata = location.state?.callMetadata;
  const isForbidden = (err) => err?.response?.status === 403 || err?.status === 403;

  useEffect(() => {
    if (callId && activeWorkspace?.id) {
      captureEvent("SET_CallDetails_PageView", {
        call_id: callId,
        workspace_id: activeWorkspace.id,
      });
    }
  }, [callId, activeWorkspace?.id, captureEvent]);

  const getCallMetadataFn = useQuery(
    ["get_call_metadata", callId],
    () => {
      const cookieName = process.env.REACT_APP_PR_ACCESS_TOKEN
        ? process.env.REACT_APP_PR_ACCESS_TOKEN
        : "accessToken";
      const token = Cookies.get(cookieName);

      return getCallMetadata({
        callId: callId,
        options: {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Workspace-Id": activeWorkspace?.id || "",
          },
        },
      });
    },
    {
      enabled: !!callId && !!activeWorkspace?.id,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        // console.log("Call metadata response:", data);
        const metadata = data?.data || data;
        setCallMetadata(metadata);
        setProviderRecordingId(metadata?.provider_recording_id);
      },
      // onError: (error) => {
      //   console.error('Call metadata API error:', error);
      //   if (error.response?.status === 403) {
      //     setAccessDenied(true);
      //   } else {
      //     setAnalysisError(error);
      //   }
      // },
      onError: (error) => {
        console.error("Call metadata API error:", error);
        if (isForbidden(error)) {
          setAccessDenied(true);
          return;
        }

        if (locationCallMetadata) {
          // console.log("Using location metadata as fallback:", locationCallMetadata);
          setCallMetadata(locationCallMetadata);
          setProviderRecordingId(locationCallMetadata?.provider_recording_id);
        } else if (recordingUrl) {
          // console.log("Creating minimal metadata with recording URL");
          const minimalMetadata = {
            recording_url: recordingUrl,
            provider_recording_id: callId,
            call_direction: "unknown",
            from_number: "Unknown",
            to_number: "Unknown",
          };
          setCallMetadata(minimalMetadata);
          setProviderRecordingId(callId);
        } else {
          setAnalysisError(error);
        }
      },
    }
  );

  const mapCallDetailsResponse = (analysisResponse, metadataResponse) => {
    if (!metadataResponse) return null;

    const analysisData = analysisResponse?.data || analysisResponse || {};
    const metadata = metadataResponse || {};
    const transcriptionGenerated = analysisResponse?.transcription_generated;

    return {
      id: metadata?.id || callId,
      call_status: metadata?.call_status || "unknown",
      duration: metadata?.connected_duration_seconds || 0,
      recording_url: metadata?.recording_url,

      user_name: analysisData?.user_name || "Unknown Contact",
      contact_name: analysisData?.user_name || "Unknown Contact",

      call_direction: metadata?.call_direction || "unknown",
      from_number: metadata?.from_number,
      to_number: metadata?.to_number,
      contact_company_number:
        metadata?.call_direction === "Outgoing" ? metadata?.to_number : metadata?.from_number,
      phone_number: metadata?.call_direction === "Outgoing" ? metadata?.to_number : metadata?.from_number,

      created_at: metadata?.start_at || metadata?.created_at,
      country_flag: analysisData?.country_flag || "",

      speaker_label: analysisData?.speaker_label || [],
      utterances: analysisData?.utterances || [],
      logs: analysisData?.logs || [],
      speaker_timeline_summary: analysisData?.speaker_timeline_summary || [],
      overview: analysisData?.overview || { topics: [], next_actions: [] },
      sentiment: analysisData?.sentiment || {},
      call_score: analysisData?.call_score,

      participants: analysisData?.participants || [],
      transcription_generated: transcriptionGenerated !== false,

      hasAnalysis: !!(analysisData?.utterances?.length > 0),
      hasRecording: !!metadata?.recording_url,
    };
  };

  const getExternalPartyPhone = (item) => {
    if (!item) return "No Number";
    if (item?.call_direction === "Outgoing") {
      return item?.to_number || "No Number";
    } else {
      return item?.from_number || "No Number";
    }
  };

  const getCallDetailsFn = useQuery(
    ["get_call_details", providerRecordingId],
    () => {
      const cookieName = process.env.REACT_APP_PR_ACCESS_TOKEN
        ? process.env.REACT_APP_PR_ACCESS_TOKEN
        : "accessToken";
      const token = Cookies.get(cookieName);

      return getCallDetails({
        callId: providerRecordingId,
        options: {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Workspace-Id": activeWorkspace?.id || "",
          },
        },
      });
    },
    {
      enabled: !!providerRecordingId && !!activeWorkspace?.id,
      retry: false,
      refetchOnWindowFocus: false,

      refetchInterval: isPollingTranscription ? 5000 : false,
      refetchIntervalInBackground: false,
      onSuccess: (data) => {
        // console.log("Call analysis response:", data);
        const status = data?.status;
        const transcriptionGenerated = data?.transcription_generated;
        const hasAnalysisData = data?.data != null;

        setTranscriptionStatus(status);
        setAnalysisNotFound(false);
        setAnalysisError(null);

        const pollingStatuses = ["pending", "queued", "initiated", "in-progress"];

        if (pollingStatuses.includes(status)) {
          setIsPollingTranscription(true);
        } else if (transcriptionGenerated === true && hasAnalysisData) {
          setIsPollingTranscription(false);
        } else {
          setIsPollingTranscription(false);
        }
      },
      onError: (error) => {
        console.error("Call analysis API error:", error);
        setIsPollingTranscription(false);

        if (isForbidden(error)) {
          setAccessDenied(true);
          return;
        }
        if (error?.response?.status === 404 || error?.status === 404) {
          setAnalysisNotFound(true);
          setAnalysisError(null);
        } else {
          setAnalysisError(error);
          setAnalysisNotFound(false);
        }
      },
    }
  );

  useEffect(() => {
    if (locationCallMetadata && !callMetadata) {
      // console.log("Initializing with location metadata:", locationCallMetadata);
      setCallMetadata(locationCallMetadata);
      setProviderRecordingId(locationCallMetadata?.provider_recording_id);
    }
  }, [locationCallMetadata, callMetadata]);

  useEffect(() => {
    return () => {
      setIsPollingTranscription(false);
    };
  }, [callId]);

  useEffect(() => {
    if (getCallDetailsFn.data && !getCallDetailsFn.isLoading) {
      const status = getCallDetailsFn.data?.status;
      const pollingStatuses = ["pending", "queued", "initiated", "in-progress"];

      if (pollingStatuses.includes(status)) {
        setIsPollingTranscription(true);
      }
    }
  }, [getCallDetailsFn.data, getCallDetailsFn.isLoading]);

  const handleDownload = async (url) => {
    if (!url) return;

    try {
      const { cleanUrl, credentials } = extractCredentialsAndCleanUrl(url);

      if (!cleanUrl) return;

      const headers = {
        Accept: "audio/mpeg, audio/wav, audio/*",
      };

      if (credentials) {
        headers["Authorization"] = `Basic ${credentials}`;
      }

      const response = await fetch(cleanUrl, {
        method: "GET",
        headers,
      });

      if (!response.ok) throw new Error("Network response was not ok");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `call-recording-${callId || "audio"}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const showRecordingOnlyMode = analysisNotFound && (callMetadata?.recording_url || recordingUrl);
  const showErrorMode = !accessDenied && analysisError && !analysisNotFound;

  const isStillLoading =
    getCallMetadataFn.isLoading ||
    getCallDetailsFn.isLoading ||
    (callMetadata && !providerRecordingId && !getCallMetadataFn.error);

  if (isStillLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-4 w-96 mb-2" />
          <Skeleton className="h-12 w-full" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 dark:bg-gray-800">
          <div className="lg:col-span-2 dark:bg-gray-800">
            <Skeleton className="h-96 w-full" />
          </div>
          <div className="lg:col-span-1 dark:bg-gray-800">
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const pollingStatuses = ["pending", "queued", "initiated", "in-progress"];
  const isTranscriptionInProgress =
    callMetadata && (pollingStatuses.includes(getCallDetailsFn.data?.status) || isPollingTranscription);

  if (isTranscriptionInProgress) {
    return (
      <div className="space-y-0">
        <CallDetailsHeader callDetails={null} callMetadata={callMetadata} />
        <TranscriptionLoadingView
          recordingUrl={callMetadata?.recording_url || recordingUrl}
          callMetadata={callMetadata}
          onDownload={() =>
            handleDownload(callMetadata?.recording_url || recordingUrl)
          }
        />
      </div>
    );
  }

  const failedOrCompletedStatuses =
    getCallDetailsFn.data?.status &&
    !pollingStatuses.includes(getCallDetailsFn.data.status) &&
    getCallDetailsFn.data.transcription_generated === false;

  if (failedOrCompletedStatuses && !isPollingTranscription) {
    return (
      <div className="space-y-0">
        <CallDetailsHeader callDetails={null} callMetadata={callMetadata} />
        <RecordingOnlyView
          recordingUrl={callMetadata?.recording_url || recordingUrl}
          callMetadata={callMetadata}
          onDownload={() =>
            handleDownload(callMetadata?.recording_url || recordingUrl)
          }
        />
      </div>
    );
  }
  if (accessDenied) {
    return (
      <div className="space-y-0">
        <AccessDeniedView callMetadata={callMetadata} />
      </div>
    );
  }
  // Handle 404 or other errors
  if (showRecordingOnlyMode || showErrorMode) {
    return (
      <div className="space-y-0">
        <CallDetailsHeader callDetails={null} callMetadata={callMetadata} />
        <RecordingOnlyView
          recordingUrl={callMetadata?.recording_url || recordingUrl}
          callMetadata={callMetadata}
          onDownload={() =>
            handleDownload(callMetadata?.recording_url || recordingUrl)
          }
        />
      </div>
    );
  }

  const callDetails = mapCallDetailsResponse(getCallDetailsFn.data, callMetadata);

  // Handle null callDetails with proper error checking
  if (!callDetails) {
    // If we have metadata but no analysis, show recording-only view
    if (callMetadata?.recording_url) {
      return (
        <div className="space-y-6">
          <CallDetailsHeader callDetails={null} callMetadata={callMetadata} />
          <RecordingOnlyView
            recordingUrl={callMetadata.recording_url}
            callMetadata={callMetadata}
            onDownload={() => handleDownload(callMetadata.recording_url)}
          />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-4 w-96 mb-2" />
          <Skeleton className="h-12 w-full" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 dark:bg-gray-800">
          <div className="lg:col-span-2 dark:bg-gray-800">
            <Skeleton className="h-96 w-full" />
          </div>
          <div className="lg:col-span-1 dark:bg-gray-800">
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CallDetailsHeader callDetails={callDetails} callMetadata={callMetadata} />

      {callDetails?.hasAnalysis ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch min-h-[612px]">
          <div className="lg:col-span-2">
            <CallTranscript
              utterances={callDetails.utterances}
              callDetails={callDetails}
              callMetadata={callMetadata}
              onDownload={() =>
                handleDownload(callDetails?.recording_url || callMetadata?.recording_url)
              }
            />
          </div>
          <div className="lg:col-span-1">
            <CallRightPanel callDetails={callDetails} />
          </div>
        </div>
      ) : (
        <RecordingOnlyView
          recordingUrl={callDetails?.recording_url || callMetadata?.recording_url}
          callMetadata={callMetadata}
          onDownload={() =>
            handleDownload(callDetails?.recording_url || callMetadata?.recording_url)
          }
        />
      )}
    </div>
  );
}
